import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DialCodeSelect } from '@/components/UI/GeoSelect';
import { useNavigate, useLocation } from 'react-router-dom';
import type { RootState } from '@/redux/common/store';
import {
  useGetOnboardingStateQuery,
  useGetTenantSettingsQuery,
  useUpdateTenantMutation,
  useUpdateChecklistStepMutation,
  useMarkSettingsVisitedMutation,
  useDeferOnboardingMutation,
  useGetConfigurationQuery,
  useUpdateUserStepMutation,
} from '@/redux/internal-services/onboardingApi';
import {
  useGetBrandConfigQuery,
  useUpdateBrandConfigMutation,
  useUploadLogoMutation,
} from '@/redux/internal-services/brandApi';
import {
  useGetMeQuery,
  useUpdateMeMutation,
  useCompleteUserOnboardingMutation,
  useGetVoiceConfigQuery,
} from '@/redux/internal-services/usersApi';
import { useListTenantRolesQuery } from '@/redux/internal-services/onboardingApi';
import OnboardingStepNav from '@/components/Onboarding/OnboardingStepNav';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import TeamRolesStep from '@/components/Onboarding/TeamRolesStep';
import CompanyInfoStep from '@/components/Onboarding/CompanyInfoStep';
import ServiceTerritoryStep from '@/components/Onboarding/ServiceTerritoryStep';
import ScopeOfWorkStep from '@/components/Onboarding/ScopeOfWorkStep';
import NotificationSettingsStep from '@/components/Onboarding/NotificationSettingsStep';
import QuietHoursStep from '@/components/Onboarding/QuietHoursStep';
import TeamOnboardingStep from '@/components/Onboarding/TeamOnboardingStep';
import { VoiceModeButton } from '@/components/Onboarding/VoiceModeButton';
import { useOnboardingVoice, type OnboardingVoiceNav } from '@/hooks/useOnboardingVoice';
import type { StepHandle, SubmitResult } from '@/components/Onboarding/stepHandle';
import ProfileStep from '@/components/Onboarding/UserOnboarding/ProfileStep';
import PhoneVerifyStep from '@/components/Onboarding/UserOnboarding/PhoneVerifyStep';
import VoiceModeStep from '@/components/Onboarding/UserOnboarding/VoiceModeStep';
import ConfirmationStep from '@/components/Onboarding/ConfirmationStep';
import VoiceIntroStep from '@/components/Onboarding/VoiceIntroStep';
import { isOnboardingVoiceEnabled } from './onboardingVoiceMode';
import { buildVoicePrefsPayload } from './voicePrefsPayload';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { setUserOnboardingCompleted, setPendingVoiceHandoff } from '@/redux/slices/sessionStateSlice';
import { roleLabel } from '@/types/common';
import { applyTheme } from '@/utils/theme';
import { setBrand } from '@/redux/slices/brandSlice';
import { socketService } from '@/services/SocketService';

// Races a promise against a fixed timeout so a hung (never resolves, never rejects) backend call
// can't strand the finish-onboarding flow forever — see handleFinishOnboarding's comment. Only
// guards against a true hang; a fast rejection/resolution still wins the race immediately.
function withTimeout<T>(promise: Promise<T>, label: string, ms = 5000): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      timer = setTimeout(() => {
        console.warn(`[Onboarding] ${label} timed out after ${ms}ms — continuing without waiting for it`);
        resolve(undefined);
      }, ms);
    }),
    // Cleared once the race settles — otherwise the timer still fires ~5s later on the happy
    // path and logs a "timed out" warning for a call that actually succeeded (and keeps running
    // after this page has unmounted).
  ]).finally(() => clearTimeout(timer));
}

// Step ids the wizard can render a body for. The ordered list, labels, and which steps are
// required all come from the backend onboarding config; only these presentational icons stay
// client-side, keyed by id with a generic fallback for any future step.
type Step =
  | 'phone_verification'
  | 'company_info'
  | 'service_territory'
  | 'scope_of_work'
  | 'brand'
  | 'notification_settings'
  | 'quiet_hours'
  | 'roles'
  | 'team_onboarding'
  // Per-user steps appended client-side after the backend (tenant) steps — these finish the
  // admin's own profile inside the same guided wizard instead of a separate flow.
  | 'profile'
  | 'voice'
  // Client-only steps — not in the backend checklist
  | 'voice_intro'       // full-screen SAM welcome page between phone verification and company_info
  | 'confirmation'      // review summary shown after backend completion agent calls finish_onboarding()
  | 'workspace_ready';  // celebration screen shown after the user confirms and creates the workspace

const KNOWN_STEPS = new Set<string>([
  'phone_verification',
  'company_info',
  'service_territory',
  'scope_of_work',
  'brand',
  'notification_settings',
  'quiet_hours',
  'roles',
  'team_onboarding',
]);

const ic = (path: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">{path}</svg>
);

const DEFAULT_ICON = <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><circle cx="10" cy="10" r="8" /></svg>;

const STEP_ICONS: Record<string, React.ReactNode> = {
  phone_verification: ic(<><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8" /><rect x="3" y="5" width="18" height="14" rx="2" /></>),
  company_info: ic(<><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></>),
  service_territory: ic(<><path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></>),
  scope_of_work: ic(<path d="M14.7 6.3a4 4 0 00-5.6 5.6L3 18v3h3l6.1-6.1a4 4 0 005.6-5.6l-2.5 2.5-2.1-2.1 2.5-2.5z" />),
  brand: ic(<><circle cx="12" cy="12" r="10" /><path d="M8 12l2 2 4-4" /></>),
  notification_settings: ic(<><path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 01-3.4 0" /></>),
  quiet_hours: ic(<path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />),
  roles: ic(<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>),
  team_onboarding: ic(<><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></>),
  profile: ic(<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  voice: ic(<><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4" /></>),
};

const KNOWN_USER_STEPS = new Set<string>(['profile', 'voice']);


const BRAND_COLORS = ['#4E38B5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const SetupWizardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const subscriptionStatus = useAppSelector((s) => s.sessionState.subscriptionStatus);
  // Once SAM welcome has fired for this user, the wizard is off-limits — a returning member
  // who lands here by URL or stale link must go straight to /chat.
  const samWelcomeShown = useAppSelector((s) => s.sessionState.samWelcomeShown);
  useEffect(() => {
    if (samWelcomeShown) navigate('/chat', { replace: true });
  }, [samWelcomeShown, navigate]);
  const { data: onboardingState } = useGetOnboardingStateQuery();
  const [updateProfile] = useUpdateTenantMutation();
  const [updateBranding, { isLoading: savingBrand }] = useUpdateTenantMutation();
  const { data: brandConfig } = useGetBrandConfigQuery();
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const [updateBrandConfig] = useUpdateBrandConfigMutation();
  const [uploadLogo, { isLoading: uploadingLogo }] = useUploadLogoMutation();
  const [updateStep] = useUpdateChecklistStepMutation();
  const [markSettingsVisited, { isLoading: launching }] = useMarkSettingsVisitedMutation();
  const [deferOnboarding, { isLoading: deferring }] = useDeferOnboardingMutation();

  // Per-user onboarding (profile + voice) — folded into this wizard as the final steps.
  const { data: me } = useGetMeQuery();
  const { data: voiceConfig } = useGetVoiceConfigQuery();
  const [updateMe, { isLoading: savingProfile }] = useUpdateMeMutation();
  const [updateUserStep] = useUpdateUserStepMutation();
  const [completeUserOnboarding, { isLoading: finishingUser }] = useCompleteUserOnboardingMutation();
  const roles = useAppSelector((s) => s.sessionState.roles);
  const role = roles[0] ?? null;
  const voiceStatus = useAppSelector((s) => s.onboardingVoice.voiceStatus);
  const isVoiceActive = voiceStatus !== 'idle' && voiceStatus !== 'error';
  const { data: tenantRolesData } = useListTenantRolesQuery();

  // Step list comes exclusively from the config catalogue. No fallbacks — if the backend
  // hasn't loaded yet we show a spinner; once loaded the wizard renders exactly what the
  // backend returns, nothing more and nothing less.
  const { data: onboardingConfig, isLoading: configLoading } = useGetConfigurationQuery('onboarding');

  const steps = useMemo(() => {
    const catalogueSetupSteps = onboardingConfig?.data?.effective_value?.setup_steps as
      | { id: string; label: string; required: boolean }[]
      | undefined;
    const catalogueUserSteps = onboardingConfig?.data?.effective_value?.user_setup_steps as
      | { id: string; label: string; required: boolean }[]
      | undefined;
    const tenantSteps = (catalogueSetupSteps ?? [])
      .filter((s) => KNOWN_STEPS.has(s.id))
      .map((s) => ({ id: s.id as Step, label: s.label, required: s.required, icon: STEP_ICONS[s.id] ?? DEFAULT_ICON }));
    const userSteps = (catalogueUserSteps ?? [])
      .filter((s) => KNOWN_USER_STEPS.has(s.id))
      .map((s) => ({ id: s.id as Step, label: s.label, required: s.required, icon: STEP_ICONS[s.id] ?? DEFAULT_ICON }));
    return [...tenantSteps, ...userSteps];
  }, [onboardingConfig]);


  const [currentStep, setCurrentStep] = useState<Step>('phone_verification');
  // Guard: auto-start voice ONCE per browser session. sessionStorage survives component
  // remounts (route changes) but resets on tab close / page refresh, so each fresh load
  // gets one auto-start. The ref is a fast synchronous check; sessionStorage backs it up
  // across remounts.
  const VOICE_STARTED_KEY = '__sc_onboarding_voice_started__';
  const voiceAutoStartedRef = useRef(!!sessionStorage.getItem(VOICE_STARTED_KEY));

  // Voice-vs-manual onboarding, decided by the mic-permission prompt on the plan page. When the
  // user denied the mic we skip the SAM welcome entirely and drop straight into the manual form.
  const voiceOnboardingEnabled = isOnboardingVoiceEnabled();
  const introOrForm: Step = voiceOnboardingEnabled ? 'voice_intro' : 'company_info';

  // Resume the wizard at the first outstanding step the backend reports, so a user who closed
  // the app mid-onboarding picks up exactly where they left off. Applied once, on initial load.
  // Guard: if the plan still isn't selected but the pre-plan steps are done, divert to plans.
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    // Wait for the step catalogue too — `steps` comes from a separate query, and the resume logic
    // below dereferences `steps[0].id`, which throws if it runs before the catalogue has loaded.
    if (resumed || !onboardingState?.data || steps.length === 0) return;
    // Returning from a later guided step (e.g. Back from per-user profile setup) — land on the
    // explicitly requested step rather than the backend's resume target.
    const requestedStep = (location.state as { step?: Step } | null)?.step;
    if (requestedStep && steps.some((s) => s.id === requestedStep)) {
      setCurrentStep(requestedStep);
      setResumed(true);
      return;
    }
    if (subscriptionStatus === 'not_selected') {
      navigate('/onboarding/plans', { replace: true });
      setResumed(true);
      return;
    }
    const resumeStep = onboardingState?.data?.resume_step as Step | null;

    const stepStatus = (id: Step): string | undefined => {
      const tenantStatus = onboardingState?.data?.steps?.find((x) => x.step === id)?.status;
      if (tenantStatus !== undefined) return tenantStatus;
      return onboardingState?.data?.user_steps?.find((x) => x.step === id)?.status;
    };

    // CRITICAL: phone_verification is a special gating step that is NOT always in the catalogue
    // setup_steps array. Handle it explicitly FIRST — if it isn't resolved yet, always show it
    // before anything else. Without this, the resume logic falls through to company_info and the
    // voice_intro intercept below would (wrongly) show the SAM welcome before phone verification.
    const phoneResolved =
      stepStatus('phone_verification') === 'completed' ||
      stepStatus('phone_verification') === 'skipped';
    if (!phoneResolved || resumeStep === 'phone_verification') {
      setCurrentStep('phone_verification');
      setResumed(true);
      return;
    }

    let targetStep: string;
    if (resumeStep && steps.some((s) => s.id === resumeStep)) {
      targetStep = resumeStep;
    } else {
      // The backend only reports resume_step while a REQUIRED step is outstanding; once all
      // required steps are resolved it returns null, even if the user is still parked on an
      // unfinished optional step (e.g. notification_settings). Don't snap back to steps[0]
      // in that case — resume at the first step (tenant or user) that isn't yet resolved.
      const firstUnresolved = steps.find(
        (s) => stepStatus(s.id) !== 'completed' && stepStatus(s.id) !== 'skipped'
      );
      targetStep = (firstUnresolved ?? steps[0]).id;
    }

    // Phone is resolved. If the user would land on company_info (first setup step) and hasn't
    // seen the SAM welcome this browser tab session, show voice_intro first.
    // voiceAutoStartedRef.current is false on fresh tab load; true once voice_intro was shown.
    const landOnVoiceIntro = targetStep === 'company_info' && !voiceAutoStartedRef.current && voiceOnboardingEnabled;
    setCurrentStep(landOnVoiceIntro ? 'voice_intro' : (targetStep as Step));
    setResumed(true);
  }, [steps, onboardingState, resumed, subscriptionStatus, navigate, location.state, voiceOnboardingEnabled]);

  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [savingPhone, setSavingPhone] = useState(false);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [brandColor, setBrandColor] = useState('#585FE3');
  const [website, setWebsite] = useState('');
  const [tagline, setTagline] = useState('');

  // ── Per-user profile + voice state (final wizard steps) ─────────────────────
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState<string | null>(null);
  const [profilePhoneVerified, setProfilePhoneVerified] = useState(false);
  const [pendingProfilePhone, setPendingProfilePhone] = useState('');
  // Controlled phone draft — lifted so the voice agent can fill it via set_profile_field.
  const [profilePhoneValue, setProfilePhoneValue] = useState('');
  // Dial code the agent set separately (defaults to US). Drives the country-code dropdown directly.
  const [profilePhoneCountryCode, setProfilePhoneCountryCode] = useState('+1');
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [interactionMode, setInteractionMode] = useState<'text' | 'voice'>('voice');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female' | null>('female');
  const [profileHydrated, setProfileHydrated] = useState(false);

  // session.role holds the tenant_role_id — resolve it to the human-readable role name.
  const profileRoleName =
    (tenantRolesData?.data ?? []).find((r) => r.tenant_role_id === role)?.name ?? roleLabel(role);

  useEffect(() => {
    if (profileHydrated || !me) return;
    setProfileName(me.name ?? '');
    setProfilePhone(me.phone);
    setProfilePhoneVerified(me.phone_verified);
    setAvatarUrl(me.profile_picture_url ?? '');
    // Onboarding pre-selects Voice & Text with a female voice. Only honor a previously-saved
    // choice once onboarding is complete (a returning user) — a fresh user's backend default is
    // 'text', which must not override the intended voice-first default here.
    setInteractionMode(me.onboarding_completed && me.interaction_mode ? me.interaction_mode : 'voice');
    setVoiceGender(me.onboarding_completed && me.voice_gender ? me.voice_gender : 'female');
    setProfileHydrated(true);
  }, [me, voiceConfig, profileHydrated]);

  // Logo and primary color live in the `brand` config (same store the app theme and
  // Settings → Brand read from) — restore them here so they persist across refresh.
  useEffect(() => {
    if (!brandConfig) return;
    if (brandConfig.logo_light_url) {
      setLogoUrl(brandConfig.logo_light_url);
      setLogoPreview(brandConfig.logo_light_url);
    }
    if (brandConfig.primary_color) setBrandColor(brandConfig.primary_color);
  }, [brandConfig]);

  useEffect(() => {
    const b = (tenantSettings?.data as Record<string, unknown> | undefined)?.branding as Record<string, string> | undefined;
    if (!b) return;
    if (b.website) setWebsite(b.website);
    if (b.tagline) setTagline(b.tagline);
  }, [tenantSettings]);

  const [error, setError] = useState('');

  const currentIndex = steps.findIndex((s) => s.id === currentStep);
  const currentRequired = steps[currentIndex]?.required ?? false;
  const canGoBack = currentIndex > 0;

  const goNext = () => {
    const next = steps[currentIndex + 1];
    if (next) setCurrentStep(next.id);
    else navigate('/chat');
  };

  const goBack = () => {
    const prev = steps[currentIndex - 1];
    if (prev) setCurrentStep(prev.id);
  };

  // Mark the current step resolved on the backend (drives resume + stage), then advance.
  // After phone_verification, always go to voice_intro (SAM welcome) not directly to company_info.
  const completeAndNext = async (step: Step) => {
    await updateStep({ step, status: 'completed' }).catch(() => {});
    if (step === 'phone_verification') { setCurrentStep(introOrForm); return; }
    goNext();
  };
  const skipAndNext = async (step: Step) => {
    await updateStep({ step, status: 'skipped' }).catch(() => {});
    if (step === 'phone_verification') { setCurrentStep(introOrForm); return; }
    goNext();
  };

  // ── Phone Verification (kept as-is; gates plan selection) ──────────────
  const handleSavePhone = async () => {
    if (!phone.trim()) return;
    setError('');
    setSavingPhone(true);
    try {
      await updateProfile({ phone: `${countryCode}${phone}` }).unwrap();
      await updateStep({ step: 'phone_verification', status: 'completed' });
      // Voice mode → SAM welcome (voice_intro) before company_info; manual mode → straight to the form.
      setCurrentStep(introOrForm);
    } catch {
      setError(t('onboarding.steps.failedToSavePhone'));
    } finally {
      setSavingPhone(false);
    }
  };

  // ── Brand ──────────────────────────────────────────────────────────────
  // Update the chosen brand color (from picker, hex input, or preset swatch) and
  // apply it app-wide immediately. Only re-theme once it's a complete hex value so
  // partial typing (e.g. "#4E") doesn't flash an invalid color.
  const pickBrandColor = useCallback((value: string) => {
    setBrandColor(value);
    if (/^#[0-9a-fA-F]{6}$/.test(value)) {
      applyTheme({ primary_color: value });
      dispatch(setBrand({ primaryColor: value }));
    }
  }, [dispatch]);

  const handleBrandNext = async () => {
    setError('');
    try {
      await updateBranding({ branding: { primary_color: brandColor, website, tagline } }).unwrap();
      // Logo + primary color also live in the `brand` config — the store the app theme and
      // Settings → Brand read from — so they persist and theme the whole app.
      await updateBrandConfig({
        primary_color: brandColor,
        ...(logoUrl ? { logo_light_url: logoUrl } : {}),
      }).unwrap();
      // Apply the chosen color as the primary theme app-wide and mirror to Redux.
      applyTheme({ primary_color: brandColor });
      dispatch(setBrand({ primaryColor: brandColor, ...(logoUrl ? { logoLightUrl: logoUrl } : {}) }));
      await updateStep({ step: 'brand', status: 'completed' });
      goNext();
    } catch {
      setError(t('common.failedToSave'));
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    // Show an instant local preview, then upload so the logo is persisted and survives refresh.
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = await uploadLogo(fd).unwrap();
      setLogoUrl(url);
      setLogoPreview(url);
      // Persist right away so the logo isn't lost if the user refreshes before clicking Next.
      await updateBrandConfig({ logo_light_url: url }).unwrap();
      dispatch(setBrand({ logoLightUrl: url }));
    } catch {
      setError(t('onboarding.steps.failedToUploadLogo'));
    }
  };

  // ── Skip to Dashboard ("explore first") ────────────────────────────────
  // Defers onboarding on the backend so the setup gate is released — the user lands on the
  // dashboard now and can finish guided setup later, instead of being routed back on next load.
  //
  // wasVoiceActive: same contract as handleFinishOnboarding — captured by the CALLER before it
  // stops the session, so leaving setup early while talking to SAM still hands off to interactive
  // mode on /chat instead of dropping the user into the text composer mid-conversation.
  const handleSkipToDashboard = async (wasVoiceActive = false) => {
    await deferOnboarding().catch(() => {});
    if (wasVoiceActive) dispatch(setPendingVoiceHandoff(true));
    navigate('/chat');
  };

  // ── Team Onboarding ─────────────────────────────────────────────────────
  // Company setup is done — continue into the per-user profile step (still in this wizard).
  const handleLaunch = async () => {
    await updateStep({ step: 'team_onboarding', status: 'completed' }).catch(() => {});
    setCurrentStep('profile');
  };

  const handleSkipTeam = async () => {
    await updateStep({ step: 'team_onboarding', status: 'skipped' }).catch(() => {});
    setCurrentStep('profile');
  };

  // ── Profile (per-user) ──────────────────────────────────────────────────
  // Save name/picture, then verify a new phone number or advance straight to the voice step.
  const handleProfileProceed = async (newPhone: string | null) => {
    await updateMe({ name: profileName.trim() || undefined, profile_picture_url: avatarUrl || undefined })
      .unwrap()
      .catch(() => {});
    if (newPhone) {
      setPendingProfilePhone(newPhone);
      setVerifyingPhone(true);
    } else {
      await updateUserStep({ step: 'profile', status: 'completed' }).catch(() => {});
      setCurrentStep('voice');
    }
  };

  const handleProfilePhoneVerified = () => {
    setProfilePhone(pendingProfilePhone);
    setProfilePhoneVerified(true);
    setVerifyingPhone(false);
    void updateUserStep({ step: 'profile', status: 'completed' }).catch(() => {});
    setCurrentStep('voice');
  };

  // ── Voice (final per-user step) ─────────────────────────────────────────
  // The real finish: persist everything and navigate directly to /chat.
  // Called from the confirmation screen ("Confirm and create workspace" button) or the voice
  // agent's open_dashboard action. Callers stop the voice session first.
  // workspace_ready celebration screen is intentionally skipped — go straight to chat.
  //
  // wasVoiceActive: was an onboarding voice ("Talk to SAM") session live at the moment the user
  // confirmed — captured by the caller BEFORE it calls stopVoiceRef.current() (which flips
  // isVoiceActive false), not read fresh in here. Carries through as pendingVoiceHandoff so
  // ChatPage auto-starts interactive mode instead of landing in text mode — see its "P4 removal"
  // comment for why this replaced the old samWelcomeShown-based auto-start.
  //
  // Each persistence call is timeout-guarded: a transient backend hiccup here previously left
  // the user stranded on this (already-torn-down) voice page forever — the agent's
  // open_dashboard tool call would succeed and speak "opening the dashboard now", but if
  // updateMe/completeUserOnboarding/markSettingsVisited hung (never resolved OR rejected) rather
  // than failing fast, the awaited chain below never reached navigate('/chat'). Errors were
  // already swallowed via .catch(() => {}) — only an outright hang was unguarded.
  const handleFinishOnboarding = async (wasVoiceActive = false) => {
    await withTimeout(
      updateMe(buildVoicePrefsPayload(interactionMode, voiceGender)).unwrap().catch(() => {}),
      'updateMe',
    );
    await withTimeout(completeUserOnboarding().unwrap().catch(() => {}), 'completeUserOnboarding');
    await withTimeout(markSettingsVisited().catch(() => {}), 'markSettingsVisited');
    dispatch(setUserOnboardingCompleted(true));
    if (wasVoiceActive) dispatch(setPendingVoiceHandoff(true));
    navigate('/chat', { replace: true });
  };

  // Kept for any remaining call-sites that reference handleGoToWorkspace directly.
  const handleGoToWorkspace = () => {
    navigate('/chat', { replace: true });
  };

  // Manual "Finish Setup" on the voice step → show the confirmation summary first (the same screen
  // the voice flow lands on), rather than jumping straight to chat. Persist the interaction choice so
  // the summary reflects it; the confirmation screen's "Let's Go!" then does the real finish.
  const handleGoToConfirmation = async () => {
    await updateMe(buildVoicePrefsPayload(interactionMode, voiceGender))
      .unwrap()
      .catch(() => {});
    setCurrentStep('confirmation');
  };

  // ── Voice mode ───────────────────────────────────────────────────────────
  // The agent drives the *currently mounted* step through this ref, and navigates via `nav`.
  const activeStepRef = useRef<StepHandle | null>(null);
  const stopVoiceRef = useRef<() => void>(() => {});
  const isLastStep = currentIndex === steps.length - 1;

  // The brand step is inline JSX (no forwardRef component), so we manually wire a StepHandle
  // into activeStepRef whenever the brand step is active. Without this, activeStepRef.current
  // is null on the brand step and voice set_color / fill_field / navigate actions are no-ops.
  // For all other steps, activeStepRef is managed by each component's ref={activeStepRef} prop.
  useEffect(() => {
    if (currentStep !== 'brand') {
      // Don't clear the ref here — for component-based steps, their useImperativeHandle sets it.
      // Clearing here would race against useImperativeHandle (effects run after commit) and
      // leave the ref null every time the parent re-renders alongside the child.
      return;
    }
    activeStepRef.current = {
      getState: () => ({ brandColor, website, tagline, hasLogo: !!logoPreview }),
      submit: async (): Promise<SubmitResult> => {
        try {
          await updateBranding({ branding: { primary_color: brandColor, website, tagline } }).unwrap();
          await updateBrandConfig({ primary_color: brandColor, ...(logoUrl ? { logo_light_url: logoUrl } : {}) }).unwrap();
          applyTheme({ primary_color: brandColor });
          dispatch(setBrand({ primaryColor: brandColor, ...(logoUrl ? { logoLightUrl: logoUrl } : {}) }));
          return { ok: true };
        } catch {
          return { ok: false, reason: 'transient', message: 'Failed to save branding.' };
        }
      },
      setField: (id: string, value: string) => {
        if (id === 'brand_color') pickBrandColor(value);
        else if (id === 'website') setWebsite(value);
        else if (id === 'tagline') setTagline(value);
      },
    };
  }, [currentStep, brandColor, website, tagline, logoPreview, logoUrl, updateBranding, updateBrandConfig, dispatch, pickBrandColor]);

  // When leaving the brand step, clear the manually-wired handle so the stale brand handle
  // is not accidentally used on the next step before that step's component mounts.
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (prevStepRef.current === 'brand' && currentStep !== 'brand') {
      activeStepRef.current = null;
    }
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // ── Direct field-update listener for profile/voice steps ─────────────────
  // When the agent is on the profile or voice step (steps 9-10), it dispatches
  // fill_field / set_interaction_mode / set_voice_gender / finish actions. These steps
  // have no StepHandle refs so we handle them directly here, same as UserOnboardingPage.
  const currentStepRef = useRef(currentStep);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  const handleFinishOnboardingRef = useRef(handleFinishOnboarding);
  useEffect(() => { handleFinishOnboardingRef.current = handleFinishOnboarding; });
  useEffect(() => {
    const onAction = (data: unknown) => {
      const step = currentStepRef.current;
      const msg = data as Record<string, unknown>;
      const action = msg.action as string;
      const target = msg.target as string;
      const value = msg.value as string;

      // 'finish' can come from ANY step (detour returns from any editable page → confirmation).
      // Other field-filling actions are only meaningful on profile/voice.
      if (action === 'finish') {
        // The current step's field-setting tools (set_color, fill_field, ...) only update local
        // component state via activeStepRef — they don't hit the API until that step's own
        // persist() runs. Unlike forward navigation ('navigate'/direction:'next' in
        // useOnboardingVoice.ts, which already awaits h.submit() first), finish_onboarding was
        // swapping to the confirmation step directly, unmounting the current step and silently
        // discarding any unsaved edit made during a detour (e.g. "change my brand color to
        // blue" while jumped in from the review screen). Persist first, same as forward nav.
        // Guard against activeStepRef.current or .submit being absent (profile/voice steps have
        // no StepHandle) — .submit?.() evaluates to `undefined`, not a Promise, in that case.
        Promise.resolve(activeStepRef.current?.submit?.()).finally(() => {
          setCurrentStep('confirmation');
        });
        return;
      }

      if (step !== 'profile' && step !== 'voice') return; // only user steps for field actions
      switch (action) {
        case 'fill_field':
          if (target === 'fullName') setProfileName(value);
          else if (target === 'phone') setProfilePhoneValue(value);
          else if (target === 'phoneCountryCode') setProfilePhoneCountryCode(value);
          break;
        case 'set_interaction_mode':
          setInteractionMode(value === 'voice_and_text' ? 'voice' : 'text');
          break;
        case 'set_voice_gender':
          if (value === 'male' || value === 'female') setVoiceGender(value);
          break;
        default:
          break;
      }
    };
    socketService.on('onboarding:action', onAction);
    return () => socketService.off('onboarding:action', onAction);
  }, []); // setters are stable; handler reads refs for currentStep and handleFinish

  const getMeta = () => ({
    // Send voice_intro as-is — the backend now has a real voice_intro page agent
    // with its own welcome prompt. For all other client-only steps (voice_intro handled above,
    // confirmation has no backend), report the step directly.
    currentStep,
    completedSteps: (onboardingState?.data?.steps ?? [])
      .filter((s) => s.status === 'completed' || s.status === 'skipped')
      .map((s) => s.step),
    // Forward the saved company name from the tenant API so the backend can inject it into
    // the confirmation-page trigger without an extra DB round-trip that might be stale.
    company_name:
      ((tenantSettings?.data as Record<string, unknown> | undefined)?.company_name as string) || '',
  });

  // Voice navigation reuses the same handlers the buttons use, so backend checklist status,
  // resume, and cache invalidation behave identically to manual clicks.
  const nav: OnboardingVoiceNav = {
    // submit() (run by the hook before this) already persisted the step's data; goNext here
    // marks the checklist step + advances. On the final step, finish via launch instead.
    goNext: () => {
      // voice_intro is a client-only step NOT in the `steps` array, so the normal
      // steps[currentIndex+1] math would jump to steps[0] (phone_verification). Route it
      // explicitly to company_info — this is SAM's go_to_company_info() landing.
      if (currentStep === 'voice_intro') { setCurrentStep('company_info'); return; }
      if (isLastStep) { stopVoiceRef.current(); void handleLaunch(); }
      else void completeAndNext(currentStep);
    },
    goBack: () => { if (canGoBack) goBack(); },
    skipStep: () => { if (!currentRequired) void skipAndNext(currentStep); },
    // SAM's own skip_to_dashboard() tool — only reachable from a live voice session, so the
    // handoff is unconditionally true here (same reasoning as openDashboard below).
    skipToDashboard: () => { stopVoiceRef.current(); void handleSkipToDashboard(true); },
    launch: () => { stopVoiceRef.current(); void handleLaunch(); },
    goToStep: (s) => { if (s === 'company_info' || steps.some((x) => x.id === s)) setCurrentStep(s as Step); },
    // Completion agent confirmed on the summary screen — end the voice session, then finish for
    // real (persist + navigate to chat). This callback only ever fires via the voice agent's own
    // open_dashboard tool (see useOnboardingVoice's dispatchAction), which requires a live voice
    // session to reach in the first place — wasVoiceActive is unconditionally true here.
    openDashboard: () => { stopVoiceRef.current(); void handleFinishOnboarding(true); },
  };

  // Provide profile/voice state for sendStateSync when the agent reaches steps 9-10.
  // Without this, activeStepRef.current is null on those steps (no StepHandle) and the
  // state_sync returns null, making the agent think all profile fields are empty.
  const getWizardUserStepState = useCallback(() => {
    if (currentStep === 'profile') {
      return { fullName: profileName, email: me?.email ?? '', phone: profilePhoneValue, avatarUrl };
    }
    if (currentStep === 'voice') {
      return {
        interactionMode: interactionMode === 'voice' ? 'voice_and_text' : 'text_only',
        voiceGender: voiceGender ?? '',
      };
    }
    return null;
  }, [currentStep, profileName, profilePhoneValue, avatarUrl, interactionMode, voiceGender, me]);

  // Text-to-SAM test input — controlled by onboarding.debug_text_input_enabled config key.
  // Distinct from chat_settings.interactive_text_input_enabled (a separate flag for the main
  // chat page) — do not confuse the two. Default is false (hidden in production); flip it on
  // per-env via the platform-admin onboarding config catalogue, no redeploy needed.
  // Pushed once by the backend on the "onboarding:config" socket event at voice-session start
  // (see OnboardingSessionManager.start()) and read here from onboardingVoice redux state —
  // not a REST call, and not required for the session to work: if the event is never received
  // the flag just stays false and the chip renders as a plain pill (safe default).
  const interactiveTextInputEnabled = useSelector(
    (state: RootState) => state.onboardingVoice.debugTextInputEnabled,
  );

  const { startVoice, stopVoice, emitFieldChange, isMuted, toggleMute, isSpeakerMuted, toggleSpeakerMute, showTranscript, toggleTranscript, sendStateSync, sendText } = useOnboardingVoice(
    getMeta,
    () => activeStepRef.current,
    nav,
    { getState: getWizardUserStepState },
  );
  useEffect(() => { stopVoiceRef.current = stopVoice; }, [stopVoice]);

  // Voice-step: when user taps a voice card in the UI while SAM is active, immediately
  // notify the agent so it switches voice and says a sample phrase in the new voice.
  const handleVoiceGenderChange = useCallback((gender: 'male' | 'female') => {
    setVoiceGender(gender);
    if (isVoiceActive && currentStep === 'voice') {
      emitFieldChange(`User selected "${gender}" voice — confirm the choice.`);
    }
  }, [isVoiceActive, currentStep, emitFieldChange]);

  // Keep a stable ref to startVoice so the effect below doesn't need it in deps.
  const startVoiceRef = useRef(startVoice);
  useEffect(() => { startVoiceRef.current = startVoice; }, [startVoice]);

  // Auto-start voice ONCE when user first lands on voice_intro — never again for this session.
  // The ref + sessionStorage double-guard prevents re-triggering across remounts AND re-renders.
  useEffect(() => {
    if (currentStep !== 'voice_intro') return;
    if (voiceAutoStartedRef.current) return;
    voiceAutoStartedRef.current = true;
    sessionStorage.setItem(VOICE_STARTED_KEY, '1');
    void startVoiceRef.current();
  }, [currentStep, VOICE_STARTED_KEY]);

  // voice_intro → company_info transition is driven by the backend:
  // SAM calls go_to_company_info() which dispatches navigate_to_step: company_info.
  // The existing useOnboardingVoice action handler (case 'navigate_to_step') calls
  // nav.goToStep('company_info') which calls setCurrentStep. No extra effect needed here.
  // hasAdvancedFromIntroRef is kept only to support the manual "Let's Go!" / "Set up manually" buttons.
  const hasAdvancedFromIntroRef = useRef(false);

  // Clear the session guard when the wizard fully unmounts (user finished or left onboarding),
  // so a future onboarding session (e.g. new tenant) gets a fresh auto-start.
  useEffect(() => {
    return () => { sessionStorage.removeItem(VOICE_STARTED_KEY); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push a state_sync the instant the wizard step actually changes. This is what lets the voice
  // agent know a navigation truly landed (it waits for this) — covers both agent-driven (goNext)
  // and hand Next/Back. Runs after React commits the new step + mounts its StepHandle, so the sync
  // carries the NEW currentStep. sendStateSync no-ops when no voice session is active.
  useEffect(() => { sendStateSync(); }, [currentStep, sendStateSync]);

  // Wait for both the onboarding state and the catalogue config before rendering.
  if (!onboardingState?.data || configLoading || steps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #1c1849 0%, #2d2b6b 100%)' }}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/40 border-t-white" />
      </div>
    );
  }

  // The "explore first" banner appears on optional steps — escape hatch to dashboard.
  const showBanner = currentStep !== 'company_info' && currentStep !== 'voice' && currentStep !== 'confirmation' && currentStep !== 'voice_intro';
  const cardWidth =
    currentStep === 'roles' || currentStep === 'notification_settings' || currentStep === 'voice'
      ? 'max-w-2xl'
      : currentStep === 'team_onboarding'
      ? 'max-w-xl'
      : 'max-w-lg';

  return (
    // Fixed-height shell: the page itself never scrolls (h-screen + overflow-hidden). The sidebar
    // stays put and only its step-list scrolls internally; the form column scrolls on its own. This
    // is more reliable than position:sticky, which body's `overflow-x:hidden` breaks.
    <div className="force-light h-screen overflow-hidden flex bg-ui-raised">
      {/* Left panel — hidden on mobile, visible on md+ (tablets, laptops, desktops). Full height;
          its step-list scrolls internally (scrollbar hidden) so the logo/heading never move. */}
      <div className="hidden md:block h-screen shrink-0">
        <LeftSidePanel className="h-full">
          <OnboardingStepNav
            heading="Let's set up your workspace"
            subtitle="Takes only few minutes. You can always change these later."
            steps={steps}
            activeId={currentStep}
          />
        </LeftSidePanel>
      </div>

      {/* Right panel — scrolls internally when the form is taller than the viewport. */}
      <div className="flex-1 flex flex-col bg-ui-raised min-w-0 overflow-hidden">
        {showBanner && (
          <div className="flex items-center justify-between gap-4 bg-emerald-50 border-b border-emerald-100 px-8 py-3">
            <div>
              <p className="text-sm font-medium text-emerald-800">Want to explore first?</p>
              <p className="text-xs text-emerald-700/80">Start using your dashboard now. You can always complete the setup later from your account settings.</p>
            </div>
            <button
              onClick={() => { const wasVoiceActive = isVoiceActive; stopVoiceRef.current(); void handleSkipToDashboard(wasVoiceActive); }}
              disabled={deferring}
              className="flex-shrink-0 bg-success text-white text-sm font-medium px-4 py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
            >
              {deferring ? 'Skipping...' : 'Skip to Dashboard'}
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto no-scrollbar">
          {/* min-h-full centers a short card but lets a tall one grow + scroll (no top clipping,
              the classic flex justify-center + overflow pitfall). */}
          <div className="min-h-full flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className={`w-full ${cardWidth} mx-auto`}>
            {/* Phone Verification */}
            {currentStep === 'phone_verification' && (
              <div className="bg-ui-card rounded-2xl p-8 shadow-sm text-center">
                <div className="w-14 h-14 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-7 h-7 text-brand-dark">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-ui-text mb-1">Add Your Phone Number</h2>
                <p className="text-ui-text2 text-sm mb-6">Your phone number helps with account recovery and team communication.</p>
                <p className="text-sm text-ui-text mb-3 text-left max-w-sm mx-auto">Phone number</p>
                <div className="flex gap-2 mb-4 max-w-sm mx-auto">
                  <DialCodeSelect value={countryCode} onChange={setCountryCode} className="w-36" />
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1 border border-ui-border rounded-lg px-3 py-2.5 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-brand-dark/40 focus:border-brand-dark"
                  />
                </div>
                {error && <p className="text-red-500 text-sm mb-3 max-w-sm mx-auto">{error}</p>}
                <button onClick={handleSavePhone} disabled={savingPhone || !phone.trim()}
                  className="w-full max-w-sm mx-auto block bg-[#1c1849] text-white py-2.5 rounded-xl text-sm font-medium hover:bg-[#2d2b6b] transition disabled:opacity-50">
                  {savingPhone ? 'Saving...' : 'Continue'}
                </button>
                {!currentRequired && (
                  <button onClick={() => skipAndNext('phone_verification')} className="mt-3 text-sm text-ui-text3 hover:text-ui-text2 transition">
                    Skip for now
                  </button>
                )}
              </div>
            )}

            {/* Voice Intro — SAM welcome screen, shown after phone verification */}
            {currentStep === 'voice_intro' && (
              <VoiceIntroStep
                voiceStatus={voiceStatus}
                isVoiceActive={isVoiceActive}
                onProceed={() => { hasAdvancedFromIntroRef.current = true; setCurrentStep('company_info'); }}
                onManual={() => { stopVoice(); hasAdvancedFromIntroRef.current = true; setCurrentStep('company_info'); }}
              />
            )}

            {/* Company Info — voice auto-starts on mount; FAB lets user stop/resume */}
            {currentStep === 'company_info' && (
              <div className="flex flex-col gap-2">
                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <CompanyInfoStep ref={activeStepRef} onNext={() => completeAndNext('company_info')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Service Territory */}
            {currentStep === 'service_territory' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <ServiceTerritoryStep ref={activeStepRef} onBack={canGoBack ? goBack : undefined} onNext={() => completeAndNext('service_territory')} onSkip={currentRequired ? undefined : () => skipAndNext('service_territory')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Scope of Work Type */}
            {currentStep === 'scope_of_work' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <ScopeOfWorkStep ref={activeStepRef} onBack={canGoBack ? goBack : undefined} onNext={() => completeAndNext('scope_of_work')} onSkip={currentRequired ? undefined : () => skipAndNext('scope_of_work')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Branding */}
            {currentStep === 'brand' && (
              <div className="bg-ui-card rounded-2xl p-8 shadow-sm">
                <h2 className="text-xl font-bold text-ui-text mb-1">Branding</h2>
                <p className="text-ui-text2 text-sm mb-6">Personalise how your brand appears to clients.</p>

                {/* Logo upload is always interactive (never disabled). */}
                <div className="mb-4">
                  <label
                    className="border-2 border-dashed border-ui-border rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-purple-300 transition"
                    htmlFor="logo-upload"
                  >
                    {uploadingLogo ? (
                      <span className="text-sm text-ui-text3">Uploading…</span>
                    ) : logoPreview ? (
                      <img src={logoPreview} alt="Logo" className="h-16 object-contain" />
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" className="w-8 h-8 mb-2">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                        </svg>
                        <span className="text-sm text-ui-text3">Upload Logo</span>
                      </>
                    )}
                  </label>
                  <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                </div>

                {/* Everything below the logo is disabled while voice is active. */}
                <div className={isVoiceActive ? 'pointer-events-none opacity-60 select-none' : ''}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-ui-text mb-2">Brand Color</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Custom color — hex code then swatch, unified into a single control */}
                    <div className="flex items-center border border-ui-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-dark/40 focus-within:border-brand-dark">
                      <input
                        type="text"
                        value={brandColor.toUpperCase()}
                        onChange={(e) => pickBrandColor(e.target.value)}
                        placeholder="#000000"
                        className="w-24 uppercase px-3 py-2 text-sm font-mono focus:outline-none bg-transparent text-ui-text"
                      />
                      <input
                        type="color"
                        value={brandColor}
                        onChange={(e) => pickBrandColor(e.target.value)}
                        className="h-9 w-9 cursor-pointer border-0 border-l border-ui-border p-0.5 bg-transparent"
                        aria-label="Custom brand color"
                      />
                    </div>
                    <div className="flex gap-1">
                      {BRAND_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => pickBrandColor(c)}
                          style={{ backgroundColor: c }}
                          className={`w-7 h-7 rounded-full transition-transform ${brandColor.toUpperCase() === c.toUpperCase() ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-medium text-ui-text mb-1.5">Website (optional)</label>
                  <input type="url" placeholder="https://yourcompany.com" value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full border border-ui-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 focus:border-brand-dark bg-ui-input text-ui-text" />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-ui-text mb-1.5">Tagline (optional)</label>
                  <input type="text" placeholder="Quality work, every time." value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    className="w-full border border-ui-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 focus:border-brand-dark bg-ui-input text-ui-text" />
                </div>

                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

                <div className="flex gap-3">
                  {canGoBack && (
                    <button onClick={goBack}
                      className="flex-1 border border-ui-border text-ui-text py-2.5 rounded-xl text-sm font-medium hover:bg-ui-hover transition flex items-center justify-center gap-1">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
                      Back
                    </button>
                  )}
                  <button onClick={handleBrandNext} disabled={savingBrand || uploadingLogo}
                    className="flex-1 bg-[#1c1849] text-white py-2.5 rounded-xl text-sm font-medium hover:bg-[#2d2b6b] transition disabled:opacity-50 flex items-center justify-center gap-1">
                    {savingBrand ? 'Saving...' : <>Next <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg></>}
                  </button>
                </div>
                {!currentRequired && (
                  <button type="button" onClick={() => skipAndNext('brand')} className="w-full mt-3 text-sm text-ui-text3 hover:text-ui-text2 transition">
                    Skip this step
                  </button>
                )}
                </div>{/* end voice-disabled wrapper */}
              </div>
            )}

            {/* Notification Settings */}
            {currentStep === 'notification_settings' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <NotificationSettingsStep ref={activeStepRef} onBack={canGoBack ? goBack : undefined} onNext={() => completeAndNext('notification_settings')} onSkip={currentRequired ? undefined : () => skipAndNext('notification_settings')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Quiet Hours */}
            {currentStep === 'quiet_hours' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <QuietHoursStep ref={activeStepRef} onBack={canGoBack ? goBack : undefined} onNext={() => completeAndNext('quiet_hours')} onSkip={currentRequired ? undefined : () => skipAndNext('quiet_hours')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Company Role */}
            {currentStep === 'roles' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <TeamRolesStep ref={activeStepRef} onBack={canGoBack ? goBack : () => {}} onNext={() => completeAndNext('roles')} onSkip={currentRequired ? undefined : () => skipAndNext('roles')} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Team Onboarding */}
            {currentStep === 'team_onboarding' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <TeamOnboardingStep ref={activeStepRef} onBack={canGoBack ? goBack : undefined} onLaunch={handleLaunch} onSkip={currentRequired ? undefined : handleSkipTeam} launching={launching} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Your Profile (per-user) — with a transient phone-verification screen */}
            {currentStep === 'profile' && !verifyingPhone && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <ProfileStep name={profileName} email={me?.email ?? ''} roleName={profileRoleName} phone={profilePhone} phoneVerified={profilePhoneVerified} avatarUrl={avatarUrl} onNameChange={setProfileName} onAvatarChange={setAvatarUrl} onProceed={handleProfileProceed} phoneValue={profilePhoneValue} onPhoneValueChange={setProfilePhoneValue} countryCodeValue={profilePhoneCountryCode} onBack={canGoBack ? goBack : undefined} saving={savingProfile} onSkip={currentRequired ? undefined : () => skipAndNext('profile')} disabled={isVoiceActive} />
                </div>
              </div>
            )}
            {currentStep === 'profile' && verifyingPhone && (
              <PhoneVerifyStep
                phone={pendingProfilePhone}
                onVerified={handleProfilePhoneVerified}
                onBack={() => setVerifyingPhone(false)}
              />
            )}

            {/* Voice & Interaction (final per-user step) */}
            {currentStep === 'voice' && (
              <div>                <div className={isVoiceActive ? 'ring-2 ring-indigo-200 rounded-2xl' : ''}>
                  <VoiceModeStep mode={interactionMode} voiceGender={voiceGender} onModeChange={setInteractionMode} onVoiceGenderChange={handleVoiceGenderChange} onBack={() => setCurrentStep('profile')} onFinish={handleGoToConfirmation} finishing={savingProfile || finishingUser || launching} disabled={isVoiceActive} />
                </div>
              </div>
            )}

            {/* Confirmation — review summary before creating workspace */}
            {currentStep === 'confirmation' && (
              <ConfirmationStep
                phase="review"
                interactionMode={interactionMode}
                voiceGender={voiceGender}
                isVoiceActive={isVoiceActive}
                onConfirm={() => { const wasVoiceActive = isVoiceActive; stopVoiceRef.current(); void handleFinishOnboarding(wasVoiceActive); }}
                onNavigate={handleGoToWorkspace}
                onGoToStep={(step) => setCurrentStep(step as Step)}
                finishing={savingProfile || finishingUser || launching}
              />
            )}

            {/* Workspace Ready — success screen after workspace is created */}
            {currentStep === 'workspace_ready' && (
              <ConfirmationStep
                phase="complete"
                interactionMode={interactionMode}
                voiceGender={voiceGender}
                onConfirm={handleGoToWorkspace}
                onNavigate={handleGoToWorkspace}
                finishing={launching}
              />
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Floating voice assistant — always visible on confirmation so the user can start/stop voice
          inline. Voice active → edit buttons hide (interact by voice). Voice off → edit buttons show.
          onSendText wires the text-to-SAM input directly inside the chip (QA/test only). */}
      {currentStep !== 'phone_verification' && currentStep !== 'voice_intro' && currentStep !== 'workspace_ready' && (
        <VoiceModeButton
          onStart={startVoice}
          onStop={stopVoice}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          isSpeakerMuted={isSpeakerMuted}
          onToggleSpeakerMute={toggleSpeakerMute}
          showTranscript={showTranscript}
          onToggleTranscript={toggleTranscript}
          onSendText={interactiveTextInputEnabled ? sendText : undefined}
        />
      )}
    </div>
  );
};

export default SetupWizardPage;
